/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import DataTable, { Column, Row } from "components/DataTable";
import apiService from "services/apiService";
import { FaTrash, FaUpload, FaEdit, FaFilter, FaEye, FaFileAlt, FaTimes, FaDownload, FaSearchPlus, FaSearchMinus } from "react-icons/fa";
import GenericToaster from "components/Toaster";
import Modal from "components/Modal";
import GenericModal from "components/Modal";
import DeletePopup from "components/DeletePopup/deletePopup";
import { useToaster } from "contexts/toasterContext";
import { convertUtcToIst } from "util/Util";
import { BannerContext } from "components/SideBar";

interface ProjectDocumentsProps {
    projectId: number;
}

const HIDDEN_FRAME_CLEANUP_MS = 60000;
const BLOB_URL_REVOKE_DELAY_MS = 1000;

const clickAnchor = (href: string, fileName: string, target?: string) => {
    const link = window.document.createElement("a");
    link.href = href;
    link.download = fileName;
    if (target) link.target = target;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
};

const downloadViaHiddenFrame = (url: string) => {
    const frame = window.document.createElement("iframe");
    frame.style.display = "none";
    frame.src = url;
    window.document.body.appendChild(frame);
    window.setTimeout(() => frame.remove(), HIDDEN_FRAME_CLEANUP_MS);
};

const downloadViaBlob = async (url: string, fileName: string) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
    const objectUrl = URL.createObjectURL(await response.blob());
    try {
        clickAnchor(objectUrl, fileName);
    } finally {
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), BLOB_URL_REVOKE_DELAY_MS);
    }
};

export default function ProjectDocuments({ projectId }: ProjectDocumentsProps) {
    const { addToast } = useToaster();

    const [documents, setDocuments] = useState<Row[]>([]);
    const [loading, setLoading] = useState(false);

    const [filterType, setFilterType] = useState("ALL");

    // Modal states
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    // View Content State (Iframe & Zoom)
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewingDoc, setViewingDoc] = useState<Row | null>(null);
    const [zoomLevel, setZoomLevel] = useState(1);

    // Form & Upload states
    const [file, setFile] = useState<File | null>(null);
    const [docType, setDocType] = useState("EMPLOYEE");
    const [selectedDoc, setSelectedDoc] = useState<Row | null>(null);

    // S3 Specific states
    const [s3Key, setS3Key] = useState<string | null>(null);
    const [isUploadingToS3, setIsUploadingToS3] = useState(false);
    const [uploadError, setUploadError] = useState("");

    const [toaster, setToaster] = useState({ show: false, message: "", type: "info" });

    const { isBannerVisible } = useContext(BannerContext);
    const dynamicHeightClass = isBannerVisible
        ? "h-[calc(100vh-211px)] min-h-[300px]"
        : "h-[calc(100vh-180px)] min-h-[300px]";
    
    const fetchDocuments = () => {
        setLoading(true);
        apiService.get(`api/pi/projects/${projectId}/documents`)
            .then((res) => {
                setDocuments(res.data);
            })
            .catch((err) => showToast(err.message || "Failed to load documents", "error"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchDocuments();
    }, [projectId]);

    const showToast = (message: string, type: "info" | "error") => {
        setToaster({ show: true, message, type });
        setTimeout(() => setToaster({ show: false, message: "", type: "info" }), 3000);
    };

    const clearFileState = () => {
        setFile(null);
        setS3Key(null);
        setUploadError("");
    };

    const closeViewModal = () => {
        setViewModalOpen(false);
        setViewingDoc(null);
        setZoomLevel(1); // Reset zoom on close
    };

    // --- FILE CHANGE HANDLER WITH DYNAMIC STORAGE CHECK ---
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            const allowedExtensions = ['.pdf', '.docx', '.txt'];
            const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

            if (!allowedExtensions.includes(fileExtension)) {
                addToast("Only .pdf, .docx, and .txt formats are allowed", "error");
                clearFileState();
                e.target.value = "";
                return;
            }

            if (selectedFile.size > 2097152) {
                addToast("Please upload file of size lesser than 2 MB", "error");
                clearFileState();
                e.target.value = "";
                return;
            }

            setFile(selectedFile);
            setS3Key(null);
            setUploadError("");

            setIsUploadingToS3(true);
            try {
                const res = await apiService.get("api/admin/storage").catch(() => ({ data: { storage: "DRIVE" } }));
                const currentStorage = res?.data?.storage || "DRIVE";

                if (currentStorage === "S3") {
                    const uploadUrlRes = await apiService.post("api/upload-url", { fileName: selectedFile.name });
                    const { uploadUrl, key } = uploadUrlRes.data || {};

                    if (!uploadUrl || !key) throw new Error("Invalid presigned URL response");

                    const putRes = await fetch(uploadUrl, { method: "PUT", body: selectedFile });
                    if (!putRes.ok) throw new Error("S3 upload failed");

                    setS3Key(key);
                }
            } catch (err) {
                console.error("Storage/Upload Error:", err);
                setUploadError("Upload failed, please re-select");
                addToast("Upload failed to secure storage, please re-select", "error");
            } finally {
                setIsUploadingToS3(false);
            }
        } else {
            clearFileState();
        }
    };

    // --- CREATE HANDLER ---
    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file || isUploadingToS3 || uploadError) return;

        const formData = new FormData();

        if (s3Key) {
            formData.append("key", s3Key);
        } else {
            formData.append("file", file);
        }
        formData.append("docType", docType);

        setLoading(true);
        apiService.post(`api/pi/projects/${projectId}/documents`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        })
            .then(() => {
                showToast("Document uploaded successfully", "info");
                setUploadModalOpen(false);
                clearFileState();
                fetchDocuments();
            })
            .catch((err) => {
                addToast(err.response?.data?.error || "Upload failed", "error");
                console.log("error=======", err);
            })
            .finally(() => setLoading(false));
    };

    // --- UPDATE HANDLER (File Replacement) ---
    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedDoc || isUploadingToS3 || uploadError) return;

        const formData = new FormData();

        if (file) {
            if (s3Key) {
                formData.append("key", s3Key);
            } else {
                formData.append("file", file);
            }
        }

        formData.append("docType", docType);

        setLoading(true);

        apiService.put(`api/pi/projects/${projectId}/documents/${selectedDoc.id}`, formData)
            .then(() => {
                showToast("Document updated successfully", "info");
                setUpdateModalOpen(false);
                clearFileState();
                setSelectedDoc(null);
                fetchDocuments();
            })
            .catch((err) => {
                addToast(err.response?.data?.error || "Update failed", "error");
                console.log(err);
            })
            .finally(() => setLoading(false));
    };

    // --- VIEW CONTENT HELPERS ---
    const handleViewContent = (row: Row) => {
        setViewingDoc(row);
        setViewModalOpen(true);
    };

    const getIframeUrl = (url: string) => {
        if (!url) return "";
        let finalUrl = url;

        if (url.includes("drive.google.com") && url.includes("/view")) {
            finalUrl = url.replace("/view", "/preview");
        }

        if (finalUrl.includes("drive.google.com")) {
            finalUrl += finalUrl.includes("?") ? "&rm=minimal" : "?rm=minimal";
        }

        return finalUrl;
    };

    const handleDownload = async () => {
        if (!viewingDoc?.filePath) {
            addToast("No file path available to download.", "error");
            return;
        }

        let downloadUrl = viewingDoc.filePath as string;
        const fileName = viewingDoc.fileName as string || "download";

        if (downloadUrl.includes("drive.google.com")) {
            const match = downloadUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
            if (match && match[1]) {
                const fileId = match[1];
                downloadUrl = `https://drive.google.com/uc?export=download&id=${fileId}`;
            }
            downloadViaHiddenFrame(downloadUrl);
            return;
        }

        try {
            await downloadViaBlob(downloadUrl, fileName);
        } catch {
            clickAnchor(downloadUrl, fileName, "_blank");
        }
    };

    const handleDelete = (row: Row) => {
        setSelectedDoc(row);
        setOpenDeleteModal(true);
    };

    const deleteDocument = (id: number) => {
        setOpenDeleteModal(false);

        apiService.delete(`api/pi/projects/${projectId}/documents/${id}`)
            .then(() => {
                showToast("Document deleted successfully", "info");
                fetchDocuments();
            })
            .catch(() => showToast("Failed to delete document", "error"));
    };

    const tableColumns: Column[] = [
        { key: "id", label: "ID", bold: true },
        { key: "fileName", label: "File Name" },
        { key: "docType", label: "Document Type" },
        {
            key: "uploadedAt",
            label: "Uploaded At",
            render: (value: any) => {
                if (!value) return <span className="font-normal text-gray-500 dark:text-[#E3E3E3]">N/A</span>;

                // Force JavaScript to treat the string as UTC by appending 'Z'
                const utcValue = typeof value === "string" && !value.endsWith("Z") ? `${value}Z` : value;
                const istObj = convertUtcToIst(utcValue);
                return (
                    <span className="font-normal text-gray-500 max-w-sm overflow-hidden text-base truncate xl:max-w-xs dark:text-[#E3E3E3]">
                        {istObj.full !== "N/A" ? `${istObj.date}, ${istObj.time}` : "N/A"}
                    </span>
                );
            }
        },
        {
            key: "actions",
            label: "Actions",
            excludeExport: true,
            actions: (row: any) => (
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleViewContent(row)}
                        className="btn-primary p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors"
                        title="View Document"
                    >
                        <FaEye className="text-current" />
                    </button>

                    <button
                        onClick={() => {
                            setSelectedDoc(row);
                            setDocType(row.docType);
                            clearFileState();
                            setUpdateModalOpen(true);
                        }}
                        className="btn-primary p-2 rounded-lg text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                        title="Update Document"
                    >
                        <FaEdit className="text-current" />
                    </button>

                    <button
                        onClick={() => handleDelete(row)}
                        className="btn-primary p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                        title="Delete Document"
                    >
                        <FaTrash />
                    </button>
                </div>
            ),
        },
    ];

    const filteredDocuments = filterType === "ALL"
        ? documents
        : documents.filter(doc => doc.docType === filterType);

    const isUpdateDisabled = loading || (!file && selectedDoc?.docType === docType);

    // Determine if the currently viewed document is hosted on S3/Cloudflare R2 rather than Google Drive
    const isS3Doc = viewingDoc?.filePath && !String(viewingDoc.filePath).includes("drive.google.com");

    return (
        <div className="bg-white dark:bg-gray-800 p-1 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
            <DataTable
                heightClass={dynamicHeightClass}
                data={filteredDocuments}
                columns={tableColumns}
                loading={loading}
                preAddButtons={
                    <>
                        <div className="relative flex-1 sm:flex-none">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500 dark:text-gray-400">
                                <FaFilter size={12} />
                            </div>
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="w-full sm:w-auto pl-8 pr-8 py-2 border rounded-lg text-sm bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                            >
                                <option value="ALL">All Documents</option>
                                <option value="EMPLOYEE">Employee</option>
                                <option value="ADMIN">Admin</option>
                            </select>
                        </div>

                        <button
                            title="Upload Document"
                            onClick={() => {
                                setDocType("EMPLOYEE");
                                clearFileState();
                                setUploadModalOpen(true);
                            }}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors whitespace-nowrap"
                        >
                            <FaUpload /> <span className="hidden sm:inline">Upload Document</span>
                        </button>
                    </>
                }
            />

            {/* --- VIEW CONTENT MODAL (Iframe Inline with Clipping Hack) --- */}
            {viewModalOpen && viewingDoc && (
                <GenericModal
                    isOpen={viewModalOpen}
                    onClose={closeViewModal}
                    customWidth="1000px"
                    disableContentScroll
                    hideCloseButton
                >
                    <div className="flex flex-col h-[70vh] bg-transparent -m-1 overflow-hidden">

                        {/* Header Card */}
                        <div className="flex items-center justify-between p-4 mb-5 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shrink-0 shadow-sm z-10">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-blue-50 text-blue-600 dark:bg-indigo-500/10 dark:text-indigo-400 rounded-xl">
                                    <FaFileAlt size={22} />
                                </div>
                                <div className="flex flex-col max-w-sm truncate">
                                    <h3 className="text-[17px] font-semibold text-gray-900 dark:text-white tracking-wide truncate">
                                        {viewingDoc?.fileName as string || "Document"}
                                    </h3>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                {/* S3 Zoom Controls */}
                                {isS3Doc && (
                                    <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-700 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 mr-2">
                                        <button
                                            onClick={() => setZoomLevel(prev => Math.max(prev - 0.2, 0.4))}
                                            className="p-1.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 rounded transition-colors"
                                            title="Zoom Out"
                                        >
                                            <FaSearchMinus size={13} />
                                        </button>
                                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 w-10 text-center tabular-nums">
                                            {Math.round(zoomLevel * 100)}%
                                        </span>
                                        <button
                                            onClick={() => setZoomLevel(prev => Math.min(prev + 0.2, 3))}
                                            className="p-1.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 rounded transition-colors"
                                            title="Zoom In"
                                        >
                                            <FaSearchPlus size={13} />
                                        </button>
                                    </div>
                                )}

                                <button
                                    onClick={handleDownload}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-indigo-500/10 dark:text-indigo-400 dark:hover:bg-indigo-500/20 rounded-lg transition-colors border border-transparent dark:hover:border-indigo-500/30"
                                    title="Download Document"
                                >
                                    <FaDownload size={14} /> <span className="hidden sm:inline">Download</span>
                                </button>

                                <button
                                    onClick={closeViewModal}
                                    className="p-2.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-700 rounded-lg transition-colors border border-transparent ml-2"
                                    title="Close"
                                >
                                    <FaTimes size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Iframe Body */}
                        <div
                            className="flex-1 min-h-0 bg-white rounded-2xl border border-gray-200 dark:border-slate-700 shadow-inner relative overflow-hidden"
                        >
                            {/* Wrap in overflow-auto if S3 to allow scrolling when zoomed in */}
                            <div className={`absolute inset-0 ${isS3Doc ? 'overflow-auto custom-scrollbar' : 'overflow-hidden'}`}>
                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    transform: isS3Doc ? `scale(${zoomLevel})` : 'none',
                                    transformOrigin: 'top center',
                                    transition: 'transform 0.2s ease-in-out'
                                }}>
                                    <iframe
                                        src={getIframeUrl(viewingDoc.filePath as string)}
                                        title="Document Viewer"
                                        allow="autoplay"
                                        className="absolute border-none"
                                        style={isS3Doc ? {
                                            width: '100%',
                                            height: '100%',
                                            border: 'none',
                                            top: 0,
                                            left: 0,
                                        } : {
                                            top: "-0px",
                                            left: "-24px",
                                            width: "calc(100% + 48px)",
                                            height: "calc(100% + 29px)",
                                            clipPath: "inset(0px 60px 0px 0px)"
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </GenericModal>
            )}

            {/* --- UPLOAD NEW DOCUMENT MODAL --- */}
            {uploadModalOpen && (
                <Modal isOpen={uploadModalOpen} onClose={() => { setUploadModalOpen(false); clearFileState(); }} header="Upload Document">
                    <form onSubmit={handleUpload} className="space-y-4 bg-white dark:bg-gray-800 p-4">
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
                                Document Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                required
                                value={docType}
                                onChange={(e) => setDocType(e.target.value)}
                                className="w-full border p-2 rounded-lg bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white outline-none"
                            >
                                <option value="EMPLOYEE">Employee</option>
                                <option value="ADMIN">Admin</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
                                File <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="file"
                                accept=".pdf,.docx,.txt"
                                onChange={handleFileChange}
                                className={`w-full border p-2 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white ${uploadError ? "border-red-500" : "border-gray-300 dark:border-gray-600"
                                    }`}
                                required
                            />
                            {uploadError ? (
                                <p className="text-xs text-red-500 mt-2">{uploadError}</p>
                            ) : (
                                <p className="text-xs text-gray-500 mt-2">Allowed types: .pdf, .docx, .txt (Max size: 2 MB)</p>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={loading || !file || isUploadingToS3 || !!uploadError}
                            className="flex items-center justify-center gap-2 w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isUploadingToS3 && <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
                            {isUploadingToS3 ? "Preparing..." : loading ? "Uploading..." : "Upload"}
                        </button>
                    </form>
                </Modal>
            )}

            {/* --- UPDATE EXISTING DOCUMENT MODAL --- */}
            {updateModalOpen && selectedDoc && (
                <Modal isOpen={updateModalOpen} onClose={() => { setUpdateModalOpen(false); clearFileState(); }} header="Update Document">
                    <form onSubmit={handleUpdate} className="space-y-4 bg-white dark:bg-gray-800 p-4">
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
                                Document Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                required
                                value={docType}
                                onChange={(e) => setDocType(e.target.value)}
                                className="w-full border p-2 rounded-lg bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white outline-none"
                            >
                                <option value="EMPLOYEE">Employee</option>
                                <option value="ADMIN">Admin</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
                                Replace File <span className="text-red-500">*</span>
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                Current file: <span className="font-semibold text-gray-700 dark:text-gray-300">{selectedDoc.fileName}</span>
                            </p>
                            <input
                                type="file"
                                accept=".pdf,.docx,.txt"
                                onChange={handleFileChange}
                                className={`w-full border p-2 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white ${uploadError ? "border-red-500" : "border-gray-300 dark:border-gray-600"
                                    }`}
                                required={selectedDoc?.docType === docType}
                            />
                            {uploadError ? (
                                <p className="text-xs text-red-500 mt-2">{uploadError}</p>
                            ) : (
                                <p className="text-xs text-gray-500 mt-2">Allowed types: .pdf, .docx, .txt (Max size: 2 MB)</p>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={isUpdateDisabled || isUploadingToS3 || !!uploadError}
                            className="flex items-center justify-center gap-2 w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isUploadingToS3 && <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
                            {isUploadingToS3 ? "Preparing..." : loading ? "Uploading..." : "Upload"}
                        </button>
                    </form>
                </Modal>
            )}

            {/* --- DELETE CONFIRMATION MODAL --- */}
            {openDeleteModal && selectedDoc && (
                <GenericModal
                    isOpen={openDeleteModal}
                    onClose={() => setOpenDeleteModal(false)}
                    header="Delete Document"
                >
                    <DeletePopup
                        handleDelete={deleteDocument}
                        onClose={() => setOpenDeleteModal(false)}
                        selectedRow={selectedDoc}
                    />
                </GenericModal>
            )}

            {toaster.show && (
                <GenericToaster message={toaster.message} onClose={() => setToaster({ ...toaster, show: false })} open={toaster.show} severity={toaster.type as any} />
            )}
        </div>
    );
}